import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Button,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import UndoIcon from "@material-ui/icons/Undo";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PrintIcon from "@material-ui/icons/Print";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoutePlans, useRoutePlanCandidates, useInvoicesDueRange } from "../hooks/useRoutePlannerData";
import { useHistory } from "react-router-dom";
import useAuthUser from "../hooks/useAuthUser";
import { createRoutePlan, updateRoutePlan, deleteRoutePlan } from "../api";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import styles from "./RoutePlannerPage.module.css";
import RouteDrawer from "../components/analytics/RouteDrawer";

const routePlannerAdminEmail = process.env.REACT_APP_ACTIVITY_LOG_ADMIN_EMAIL || "paul@example.com";

const toISODate = (date) => {
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) return "";
  const year = dt.getUTCFullYear();
  const month = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const todayISO = () => toISODate(new Date());
const addDays = (base, days) => {
  const dt = new Date(base);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
};
const toCalendarDate = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
};
const startOfWeek = (date) => {
  const dt = new Date(date);
  const day = dt.getDay();
  const diff = (day + 6) % 7; // Monday as first day
  dt.setDate(dt.getDate() - diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatShortDate = (value) => {
  const date = toCalendarDate(value);
  if (!date) return "Unscheduled";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const statusOptions = [
  { value: "planned", label: "Planned" },
  { value: "loading", label: "Loading" },
  { value: "enroute", label: "En Route" },
  { value: "completed", label: "Completed" },
];

const serializeStops = (stops = []) =>
  stops
    .map((stop, index) => ({
      invoice:
        typeof stop.invoice === "object"
          ? stop.invoice?._id || stop.invoice?.id
          : stop.invoice,
      order: index,
      notes: stop.notes || "",
      status: stop.status || "pending",
    }))
    .filter((stop) => Boolean(stop.invoice));

const stopStatusColorMap = {
  pending: "default",
  loaded: "primary",
  delivered: "secondary",
};

const getInvoiceId = (stop) => {
  if (!stop) return null;
  if (typeof stop.invoice === "string") return stop.invoice;
  if (typeof stop.invoice === "object")
    return stop.invoice?._id || stop.invoice?.id || stop.invoice?.invoiceNumber || null;
  return null;
};

const getStopDisplayName = (stop) =>
  stop?.invoice?.client?.store ||
  stop?.invoice?.client?.name ||
  "Invoice";

const getInvoiceAmount = (invoice) =>
  invoice?.total ?? invoice?.subTotal ?? invoice?.amount ?? null;

const getInvoiceQuantity = (invoice) =>
  (invoice?.items || []).reduce(
    (sum, item) => sum + (Number(item?.quantity) || 0),
    0
  );

const getInvoiceProductBreakdown = (invoice) => {
  const map = new Map();
  (invoice?.items || []).forEach((item) => {
    const key = item?.modelNumber || item?.name || item?.productName || "Item";
    const qty = Number(item?.quantity) || 0;
    if (!qty) return;
    map.set(key, (map.get(key) || 0) + qty);
  });
  return Array.from(map.entries()).map(([label, qty]) => ({ label, qty }));
};

const PlanStopList = ({
  plan,
  droppableId,
  onStatusChange,
  onRemoveStop,
  onMoveStop,
  disabled,
}) => (
  <Droppable droppableId={droppableId}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        style={{
          backgroundColor: snapshot.isDraggingOver ? "#f1f5ff" : "#fafafa",
          borderRadius: 8,
          padding: plan.stops?.length ? 8 : 0,
          minHeight: 48,
        }}
      >
        {plan.stops?.length ? (
          <List dense disablePadding>
            {plan.stops.map((stop, index) => {
              const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
              const draggableId = `${plan._id}-${getInvoiceId(stop) || index}-${index}-${droppableId}`;
            const amount = getInvoiceAmount(invoice);
            const quantity = getInvoiceQuantity(invoice);
            const breakdown = getInvoiceProductBreakdown(invoice);
            const status = stop.status || "pending";
            return (
              <Draggable key={draggableId} draggableId={draggableId} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <ListItem
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      style={{
                        backgroundColor: dragSnapshot.isDragging ? "#e6edff" : "white",
                        borderRadius: 8,
                        marginBottom: 8,
                        boxShadow: dragSnapshot.isDragging
                          ? "0 4px 12px rgba(0,0,0,0.1)"
                          : "none",
                        ...dragProvided.draggableProps.style,
                      }}
                      alignItems="flex-start"
                    >
                      <Box
                        mr={2}
                        mt={0.5}
                        {...dragProvided.dragHandleProps}
                        style={{ cursor: "grab", color: "#9e9e9e" }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </Box>
                      <Box flexGrow={1}>
                        <Typography variant="subtitle2">
                          {getStopDisplayName(stop)}
                        </Typography>
                        {invoice?.client?.address && (
                          <Typography variant="body2" color="textSecondary">
                            {invoice.client.address}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          {formatShortDate(invoice?.receivedByDate)} • {formatCurrency(amount)}
                          {quantity ? ` • Qty ${quantity}` : ""}
                        </Typography>
                        {!!breakdown.length && (
                          <Box component="ul" className={styles.breakdownList}>
                            {breakdown.map((item) => (
                              <li key={item.label}>
                                <Typography variant="caption">{`${item.qty} × ${item.label}`}</Typography>
                              </li>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <Chip
                          size="small"
                          label={status}
                          color={stopStatusColorMap[status] || "default"}
                        />
                        {onStatusChange && (
                          <Tooltip
                            title={status === "delivered" ? "Mark pending" : "Mark delivered"}
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onStatusChange(plan._id, index, status === "delivered" ? "pending" : "delivered")
                                }
                                disabled={disabled}
                              >
                                {status === "delivered" ? (
                                  <UndoIcon fontSize="small" />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onMoveStop && (
                          <Tooltip title="Move to another route">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onMoveStop(plan, index)}
                                disabled={disabled}
                              >
                                <SwapHorizIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onRemoveStop && (
                          <Tooltip title="Remove from route">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onRemoveStop(plan._id, index)}
                                disabled={disabled}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  )}
                </Draggable>
              );
            })}
          </List>
        ) : (
          <Box
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="textSecondary"
          >
            <Typography variant="body2" color="textSecondary">
              No stops yet. Use “Add stops” to assign invoices.
            </Typography>
          </Box>
        )}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);
const RouteCard = ({
  plan,
  onEdit,
  onAssignStops,
  assignDisabled,
  onOpenDrawer,
  onMarkLoaded,
  onDeleteRoute,
  deleteDisabled,
  children,
}) => {
  const routeDate = formatShortDate(plan.routeDate);
  const stopCount = plan.stops?.length || 0;
  const totalAmount =
    typeof plan.totalAmount === "number"
      ? plan.totalAmount
      : plan.stops?.reduce((sum, stop) => {
          const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
          const amount = invoice?.total ?? invoice?.subTotal ?? 0;
          return sum + (Number(amount) || 0);
        }, 0) || 0;
  const totalQuantity =
    plan.stops?.reduce((sum, stop) => {
      const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
      return sum + getInvoiceQuantity(invoice);
    }, 0) || 0;
  const productBreakdown = React.useMemo(() => {
    const map = new Map();
    (plan.stops || []).forEach((stop) => {
      const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
      getInvoiceProductBreakdown(invoice).forEach(({ label, qty }) => {
        map.set(label, (map.get(label) || 0) + qty);
      });
    });
    return Array.from(map.entries())
      .map(([label, qty]) => ({ label, qty }))
      .sort((a, b) => b.qty - a.qty);
  }, [plan.stops]);

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {plan.name || `Route on ${routeDate}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {routeDate}
            </Typography>
          </Box>
        }
        subheader={routeDate}
        action={
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip
              size="small"
              label={plan.status || "planned"}
              color={
                plan.status === "completed"
                  ? "primary"
                  : plan.status === "enroute"
                  ? "secondary"
                  : "default"
              }
            />
            <Tooltip title="Mark loaded">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onMarkLoaded?.(plan)}
                  disabled={
                    plan.status === "completed" ||
                    plan.status === "loading" ||
                    !onMarkLoaded
                  }
                >
                  <AssignmentTurnedInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Print load sheet">
              <span>
                <IconButton
                  size="small"
                  onClick={() => window.open(`/routes/${plan._id}?print=1`, "_blank")}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Open details">
              <span>
                <IconButton size="small" onClick={() => onOpenDrawer?.(plan)}>
                  <LocalShippingIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {onEdit && (
              <Button size="small" onClick={() => onEdit(plan)}>
                Edit
              </Button>
            )}
            {onDeleteRoute && (
              <Tooltip title="Delete route">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteRoute(plan)}
                    disabled={deleteDisabled}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        }
      />
      <CardContent>
        <Box display="flex" flexWrap="wrap" style={{ gap: 16, marginBottom: 16 }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Stops
            </Typography>
            <Typography variant="h6">{stopCount}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Load value
            </Typography>
            <Typography variant="h6">{formatCurrency(totalAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Quantity
            </Typography>
            <Typography variant="h6">{totalQuantity}</Typography>
          </Box>
          {plan.driverName && (
            <Box>
              <Typography variant="caption" color="textSecondary">
                Driver
              </Typography>
              <Typography variant="body1">{plan.driverName}</Typography>
            </Box>
          )}
        </Box>
        {plan.notes && (
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Notes: {plan.notes}
          </Typography>
        )}
        {!!productBreakdown.length && (
          <Box mb={2}>
            <Typography variant="caption" color="textSecondary">
              Items on this route
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 4 }}>
              {productBreakdown.slice(0, 6).map((item) => (
                <Chip key={item.label} label={`${item.qty} × ${item.label}`} />
              ))}
            </Box>
          </Box>
        )}
        <Button
          size="small"
          color="primary"
          onClick={() => onAssignStops?.(plan)}
          disabled={assignDisabled}
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
        >
          Add stops
        </Button>
        <Box mt={2}>{children}</Box>
      </CardContent>
    </Card>
  );
};

const CandidateListCard = ({
  invoices,
  readyInvoices,
  stagedInvoiceIds,
  loading,
  refreshing,
  onToggleStage,
}) => (
  <Card variant="outlined">
    <CardHeader
      title="Unassigned invoices"
      subheader={
        refreshing && !loading
          ? "Refreshing…"
          : "Invoices that are not yet on any route plan."
      }
    />
    <CardContent style={{ paddingTop: 0, display: "flex", flexDirection: "column", gap: 16 }}>
      <Box>
        <Typography variant="h4">{loading ? "…" : invoices.length}</Typography>
        <Typography variant="body2" color="textSecondary">
          Total unassigned invoices in current range
        </Typography>
      </Box>
      <Box>
        <Typography variant="subtitle1">Ready to load</Typography>
        {loading ? (
          <Typography color="textSecondary">Loading invoices…</Typography>
        ) : !readyInvoices.length ? (
          <Typography color="textSecondary">
            No invoices look ready in this window. Try adjusting filters.
          </Typography>
        ) : (
          <List dense disablePadding style={{ maxHeight: 320, overflowY: "auto" }}>
            {readyInvoices.map((invoice, index) => {
              const staged = stagedInvoiceIds.includes(String(invoice._id));
              const quantity = getInvoiceQuantity(invoice);
              return (
                <React.Fragment key={invoice._id || index}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        invoice.client?.store ||
                        invoice.client?.name ||
                        "Invoice"
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="textSecondary">
                            {formatCurrency(invoice.total)} • Due {formatShortDate(invoice.dueDate)}
                            {quantity ? ` • Qty ${quantity}` : ""}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Status: {invoice.status || "Unknown"}
                          </Typography>
                        </>
                      }
                    />
                    <Button
                      size="small"
                      variant={staged ? "contained" : "outlined"}
                      color={staged ? "primary" : "default"}
                      onClick={() => onToggleStage(String(invoice._id))}
                    >
                      {staged ? "Selected" : "Stage"}
                    </Button>
                  </ListItem>
                  {index < readyInvoices.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
        {!!readyInvoices.length && (
          <Typography variant="caption" color="textSecondary">
            Staged invoices will auto-populate the Add stops dialog.
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const RouteEditorDialog = ({
  open,
  initialValues,
  onClose,
  onSubmit,
  isSaving,
  errorMessage,
  defaultDate,
}) => {
  const [formState, setFormState] = React.useState({
    routeDate: todayISO(),
    name: "",
    driverName: "",
    status: "planned",
    notes: "",
  });

  React.useEffect(() => {
    const dateValue = initialValues?.routeDate
      ? toISODate(initialValues.routeDate)
      : defaultDate || todayISO();
    setFormState({
      routeDate: dateValue,
      name: initialValues?.name || "",
      driverName: initialValues?.driverName || "",
      status: initialValues?.status || "planned",
      notes: initialValues?.notes || "",
    });
  }, [initialValues, open, defaultDate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formState);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initialValues ? "Edit route" : "Create route"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <TextField
              label="Route date"
              type="date"
              name="routeDate"
              value={formState.routeDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Route name"
              name="name"
              value={formState.name}
              onChange={handleChange}
              placeholder="Optional label"
            />
            <TextField
              label="Driver"
              name="driverName"
              value={formState.driverName}
              onChange={handleChange}
            />
            <TextField
              select
              label="Status"
              name="status"
              value={formState.status}
              onChange={handleChange}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes"
              name="notes"
              value={formState.notes}
              onChange={handleChange}
              multiline
              rows={3}
            />
            {errorMessage && (
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={isSaving}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const AssignInvoicesDialog = ({
  open,
  plan,
  invoices,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
  initialSelection = [],
}) => {
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const optionMap = React.useMemo(() => {
    const map = new Map();
    invoices.forEach((invoice) => {
      map.set(String(invoice._id), invoice);
    });
    return map;
  }, [invoices]);

  React.useEffect(() => {
    if (open) {
      const seeds =
        (initialSelection || [])
          .map((id) => optionMap.get(String(id)))
          .filter(Boolean) || [];
      setSelectedOptions(seeds);
      setInputValue("");
    }
  }, [initialSelection, optionMap, open]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedOptions.length) return;
    onSubmit(selectedOptions.map((option) => option._id));
  };

  const renderOptionContent = (option) => (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      style={{ borderBottom: "1px solid rgba(0,0,0,0.08)", paddingBottom: 8, marginBottom: 8 }}
    >
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        {option.client?.store || option.client?.name || "Invoice"}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {formatCurrency(option.total)} • Due {formatShortDate(option.dueDate)} • Status{" "}
        {option.status || "Unknown"}
        {(() => {
          const quantity = getInvoiceQuantity(option);
          return quantity ? ` • Qty ${quantity}` : "";
        })()}
      </Typography>
      {(() => {
        const breakdown = getInvoiceProductBreakdown(option);
        if (!breakdown.length) return null;
        return (
          <Box component="ul" className={styles.breakdownList} style={{ marginTop: 2 }}>
            {breakdown.map((item) => (
              <li key={item.label}>
                <Typography variant="caption">{`${item.qty} × ${item.label}`}</Typography>
              </li>
            ))}
          </Box>
        );
      })()}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Assign invoices{plan?.name ? ` to ${plan.name}` : ""}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {invoices.length === 0 ? (
            <Typography color="textSecondary">
              All invoices in the selected date range are already on routes.
            </Typography>
          ) : (
            <>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={invoices}
                value={selectedOptions}
                onChange={(event, newValue) => setSelectedOptions(newValue)}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                getOptionSelected={(option, value) => option._id === value._id}
                getOptionLabel={(option) =>
                  option?.client?.store ||
                  option?.client?.name ||
                  `Invoice ${option?._id}`
                }
                renderOption={(option) => renderOptionContent(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search invoices"
                    placeholder="Type store, customer, PO, or invoice number"
                    variant="outlined"
                    margin="normal"
                  />
                )}
              />
              {selectedOptions.length > 0 && (
                <Box mt={1}>
                  <Typography variant="subtitle2">
                    Selected ({selectedOptions.length})
                  </Typography>
                  <List dense style={{ maxHeight: 160, overflowY: "auto" }}>
                    {selectedOptions.map((invoice) => (
                      <React.Fragment key={invoice._id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              invoice.client?.store ||
                              invoice.client?.name ||
                              "Invoice"
                            }
                            secondary={`${formatCurrency(invoice.total)} • Due ${formatShortDate(
                              invoice.dueDate
                            )}${(() => {
                              const quantity = getInvoiceQuantity(invoice);
                              return quantity ? ` • Qty ${quantity}` : "";
                            })()}`}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
              {errorMessage && (
                <Typography
                  color="error"
                  variant="body2"
                  style={{ marginTop: 8 }}
                >
                  {errorMessage}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={!selectedOptions.length || isSubmitting || !invoices.length}
          >
            {isSubmitting ? "Assigning…" : `Assign (${selectedOptions.length})`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const MoveStopDialog = ({
  open,
  fromPlan,
  stopIndex,
  routes,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [targetRouteId, setTargetRouteId] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setTargetRouteId("");
    }
  }, [open, fromPlan?._id]);

  const availableRoutes = React.useMemo(
    () => routes.filter((route) => route._id !== fromPlan?._id),
    [routes, fromPlan?._id]
  );

  const stop = fromPlan?.stops?.[stopIndex];
  const stopLabel =
    stop?.invoice?.client?.store ||
    stop?.invoice?.client?.name ||
    "Invoice";

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!targetRouteId) return;
    onSubmit(targetRouteId);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Move stop</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {stop ? `Move ${stopLabel} to another route.` : "Select a destination route."}
          </Typography>
          <TextField
            select
            label="Destination route"
            value={targetRouteId}
            onChange={(e) => setTargetRouteId(e.target.value)}
            fullWidth
            disabled={!availableRoutes.length || isSubmitting}
            helperText={
              availableRoutes.length
                ? "Choose a route that already exists in the current view."
                : "No other routes available."
            }
          >
            {availableRoutes.map((route) => (
              <MenuItem key={route._id} value={route._id}>
                {route.name || formatShortDate(route.routeDate)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={!targetRouteId || isSubmitting}
          >
            {isSubmitting ? "Moving…" : "Move stop"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const groupPlansByDate = (plans = []) => {
  const map = plans.reduce((acc, plan) => {
    const key = plan.routeDate ? toISODate(plan.routeDate) || "unscheduled" : "unscheduled";
    if (!acc[key]) acc[key] = [];
    acc[key].push(plan);
    return acc;
  }, {});

  return Object.entries(map)
    .sort((a, b) => {
      if (a[0] === "unscheduled") return 1;
      if (b[0] === "unscheduled") return -1;
      return new Date(a[0]) - new Date(b[0]);
    })
    .map(([dateKey, dayPlans]) => ({
      dateKey,
      label: dateKey === "unscheduled" ? "Unscheduled" : formatShortDate(dateKey),
      plans: dayPlans,
    }));
};

const RoutePlannerPage = () => {
  const initialWeekStart = React.useMemo(() => startOfWeek(new Date()), []);
  const initialWeekEnd = React.useMemo(() => {
    const dt = new Date(initialWeekStart);
    dt.setDate(dt.getDate() + 6);
    return dt;
  }, [initialWeekStart]);
  const [fromDate, setFromDate] = React.useState(() => toISODate(initialWeekStart));
  const [toDate, setToDate] = React.useState(() => toISODate(initialWeekEnd));
  const [statusFilter, setStatusFilter] = React.useState([]);
  const [editorState, setEditorState] = React.useState({ open: false, plan: null, defaultDate: todayISO() });
  const [editorError, setEditorError] = React.useState(null);
  const [assignState, setAssignState] = React.useState({ open: false, plan: null, initialSelection: [] });
  const [assignError, setAssignError] = React.useState(null);
  const [moveState, setMoveState] = React.useState({ open: false, fromPlan: null, stopIndex: null });
  const [stopActionError, setStopActionError] = React.useState(null);
  const [activePreset, setActivePreset] = React.useState("week");
  const [selectedDay, setSelectedDay] = React.useState(todayISO());
  const [stagedInvoiceIds, setStagedInvoiceIds] = React.useState([]);
  const [drawerState, setDrawerState] = React.useState({ open: false, planId: null });
  const [calendarAnchorDate, setCalendarAnchorDate] = React.useState(() => new Date());
  const [calendarView, setCalendarView] = React.useState(() => {
    if (typeof window === "undefined") return "dayGridMonth";
    return window.innerWidth < 768 ? "listWeek" : "dayGridMonth";
  });
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => {
      const desired = window.innerWidth < 768 ? "listWeek" : "dayGridMonth";
      setCalendarView((prev) => (prev === desired ? prev : desired));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const calendarRef = React.useRef(null);
  const openRouteDrawer = React.useCallback((planId) => {
    if (!planId) return;
    setDrawerState({ open: true, planId });
  }, []);
  const closeRouteDrawer = React.useCallback(() => setDrawerState({ open: false, planId: null }), []);
  const quickFilterPresets = React.useMemo(
    () => [
      { id: "today", label: "Today" },
      { id: "tomorrow", label: "Tomorrow" },
      { id: "week", label: "This Week" },
      { id: "week_next", label: "Next Week" },
      { id: "month", label: "This Month" },
      { id: "unassigned", label: "Unassigned" },
    ],
    []
  );

  const history = useHistory();
  const authData = useAuthUser();
  const isRoutePlannerAdmin = authData?.result?.email === routePlannerAdminEmail;
  const currentUserId = authData?.result?._id || authData?.result?.googleId || null;

  React.useEffect(() => {
    if (!authData) {
      history.push("/login");
      return;
    }
    if (!isRoutePlannerAdmin) {
      history.push("/dashboard");
    }
  }, [authData, history, isRoutePlannerAdmin]);

  const queryClient = useQueryClient();

  const routePlansQuery = useRoutePlans({
    from: fromDate,
    to: toDate,
    status: statusFilter,
    enabled: isRoutePlannerAdmin,
  });

  const routeCandidatesQuery = useRoutePlanCandidates({
    from: fromDate,
    to: toDate,
    enabled: isRoutePlannerAdmin,
  });

  const plans = routePlansQuery.data || [];
  const groupedPlans = React.useMemo(() => groupPlansByDate(plans), [plans]);
  const candidateInvoices = routeCandidatesQuery.data || [];
  const plansById = React.useMemo(() => {
    const map = new Map();
    plans.forEach((plan) => {
      if (plan?._id) {
        map.set(plan._id, plan);
      }
    });
    return map;
  }, [plans]);
  const todayIso = React.useMemo(() => todayISO(), []);
  const activeDrawerPlan = drawerState.planId ? plansById.get(drawerState.planId) : null;
  const calendarMonthRange = React.useMemo(() => {
    const anchor = toCalendarDate(calendarAnchorDate);
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: toISODate(start), to: toISODate(end) };
  }, [calendarAnchorDate]);
  const invoicesDueQuery = useInvoicesDueRange({
    from: calendarMonthRange.from,
    to: calendarMonthRange.to,
    enabled: isRoutePlannerAdmin,
  });
  const dueInvoices = invoicesDueQuery.data || [];
  const calendarPlansQuery = useRoutePlans({
    from: calendarMonthRange.from,
    to: calendarMonthRange.to,
    enabled: isRoutePlannerAdmin,
  });
  const calendarPlans = calendarPlansQuery.data || [];
  const routeCalendarEvents = React.useMemo(
    () =>
      calendarPlans
        .map((plan) => {
          if (!plan.routeDate) return null;
          const iso = toISODate(plan.routeDate);
          if (!iso) return null;
          const stopCount = plan.stops?.length || 0;
          const stopsLabel = stopCount ? `${stopCount} stop${stopCount === 1 ? "" : "s"}` : "No stops";
          return {
            id: plan._id || `${iso}-${stopsLabel}`,
            title: plan.name ? `${plan.name} (${stopsLabel})` : stopsLabel,
            date: iso,
            extendedProps: { planId: plan._id },
            color: iso < todayIso ? "#bdbdbd" : undefined,
            textColor: iso < todayIso ? "#424242" : undefined,
          };
        })
        .filter(Boolean),
    [calendarPlans, todayIso]
  );
  const invoiceCalendarEvents = React.useMemo(
    () =>
      dueInvoices
        .map((invoice) => {
          if (!invoice?.dueDate) return null;
          const iso = toISODate(invoice.dueDate);
          if (!iso) return null;
          const quantity = getInvoiceQuantity(invoice);
          const titleParts = [
            invoice.client?.store || invoice.client?.name || "Invoice",
            quantity ? `${quantity} items` : null,
            formatCurrency(getInvoiceAmount(invoice)),
          ].filter(Boolean);
          return {
            id: `due-${invoice._id || iso}`,
            title: titleParts.join(" • "),
            date: iso,
            backgroundColor: "#ffcc80",
            borderColor: "#ff9800",
            textColor: "#5d4037",
          };
        })
        .filter(Boolean),
    [dueInvoices]
  );
  const calendarEvents = React.useMemo(
    () => [...routeCalendarEvents, ...invoiceCalendarEvents],
    [routeCalendarEvents, invoiceCalendarEvents]
  );
  const focusCalendarDate = React.useCallback((dateObj) => {
    if (!dateObj || !calendarRef.current) return;
    const api = calendarRef.current.getApi?.();
    if (api) {
      api.gotoDate(dateObj);
      setCalendarAnchorDate(api.getDate());
    }
  }, []);
  React.useEffect(() => {
    const api = calendarRef.current?.getApi?.();
    if (api && api.view?.type !== calendarView) {
      api.changeView(calendarView);
      setCalendarAnchorDate(api.getDate());
    }
  }, [calendarView]);
  const handlePresetSelect = (presetId) => {
    const now = new Date();
    let nextFrom = fromDate;
    let nextTo = toDate;
    let nextSelectedDay = null;
    switch (presetId) {
      case "today": {
        const target = now;
        const iso = toISODate(target);
        nextFrom = iso;
        nextTo = iso;
        nextSelectedDay = iso;
        focusCalendarDate(target);
        break;
      }
      case "tomorrow": {
        const target = new Date(now);
        target.setDate(target.getDate() + 1);
        const iso = toISODate(target);
        nextFrom = iso;
        nextTo = iso;
        nextSelectedDay = iso;
        focusCalendarDate(target);
        break;
      }
      case "week": {
        const start = startOfWeek(now);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        nextFrom = toISODate(start);
        nextTo = toISODate(end);
        nextSelectedDay = null;
        focusCalendarDate(start);
        break;
      }
      case "week_next": {
        const target = new Date(now);
        target.setDate(target.getDate() + 7);
        const start = startOfWeek(target);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        nextFrom = toISODate(start);
        nextTo = toISODate(end);
        nextSelectedDay = null;
        focusCalendarDate(start);
        break;
      }
      case "month": {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        nextFrom = toISODate(start);
        nextTo = toISODate(end);
        nextSelectedDay = null;
        focusCalendarDate(start);
        break;
      }
      case "unassigned": {
        nextFrom = "";
        nextTo = "";
        nextSelectedDay = null;
        break;
      }
      default:
        break;
    }
    setFromDate(nextFrom);
    setToDate(nextTo);
    setSelectedDay(nextSelectedDay);
    setActivePreset(presetId);
    clearCalendarSelection();
  };
  const clearCalendarSelection = React.useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.unselect();
    }
  }, []);
  const handleCalendarDaySelect = React.useCallback(
    (iso) => {
      setFromDate(iso);
      setToDate(iso);
      setSelectedDay(iso);
      setActivePreset(`day:${iso}`);
      focusCalendarDate(new Date(iso));
      clearCalendarSelection();
    },
    [clearCalendarSelection, focusCalendarDate]
  );
  const handleFromDateInput = (value) => {
    setFromDate(value);
    if (value && value === toDate) {
      setSelectedDay(value);
      setActivePreset(`day:${value}`);
      focusCalendarDate(new Date(value));
      clearCalendarSelection();
    } else {
      setSelectedDay(null);
      setActivePreset(null);
    }
  };
  const handleCalendarDateClick = React.useCallback(
    (arg) => {
      const iso = toISODate(arg.date);
      if (iso) {
        handleCalendarDaySelect(iso);
      }
    },
    [handleCalendarDaySelect]
  );
  const handleEventClick = React.useCallback(
    (clickInfo) => {
      const iso = clickInfo.event.start ? toISODate(clickInfo.event.start) : null;
      if (iso) {
        handleCalendarDaySelect(iso);
      }
      const planId = clickInfo.event.extendedProps?.planId;
      if (planId) {
        openRouteDrawer(planId);
      }
    },
    [handleCalendarDaySelect, openRouteDrawer]
  );
  const handleCalendarRangeSelect = React.useCallback(
    (selectionInfo) => {
      if (!selectionInfo || !selectionInfo.start || !selectionInfo.end) return;
      const startIso = toISODate(selectionInfo.start);
      const exclusiveEnd = new Date(selectionInfo.end);
      exclusiveEnd.setDate(exclusiveEnd.getDate() - 1);
      const endIso = toISODate(exclusiveEnd);
      if (!startIso || !endIso) return;
      setFromDate(startIso);
      setToDate(endIso);
      setSelectedDay(null);
      setActivePreset(null);
      focusCalendarDate(selectionInfo.start);
    },
    [focusCalendarDate]
  );
  const dayCellClassNames = React.useCallback(
    (arg) => {
      const iso = toISODate(arg.date);
      const classes = [];
      if (selectedDay && iso === selectedDay) {
        classes.push(styles.selectedDayCell);
      }
      if (fromDate || toDate) {
        const normalize = (dateLike) =>
          dateLike ? toCalendarDate(dateLike).setHours(0, 0, 0, 0) : null;
        const dayTime = toCalendarDate(arg.date).setHours(0, 0, 0, 0);
        const fromTime = normalize(fromDate);
        const toTime = normalize(toDate);
        const inRange =
          (fromTime === null || dayTime >= fromTime) &&
          (toTime === null || dayTime <= toTime);
        if (inRange) {
          classes.push(styles.rangeDayCell);
        }
        if (fromTime !== null && dayTime === fromTime) {
          classes.push(styles.rangeStartCell);
        }
        if (toTime !== null && dayTime === toTime) {
          classes.push(styles.rangeEndCell);
        }
      }
      return classes;
    },
    [selectedDay, fromDate, toDate]
  );
  const handleCalendarPrev = React.useCallback(() => {
    const api = calendarRef.current?.getApi?.();
    api?.prev();
    setSelectedDay(null);
    setCalendarAnchorDate(api?.getDate?.() || new Date());
  }, []);
  const handleCalendarNext = React.useCallback(() => {
    const api = calendarRef.current?.getApi?.();
    api?.next();
    setSelectedDay(null);
    setCalendarAnchorDate(api?.getDate?.() || new Date());
  }, []);
  const toggleStagedInvoice = (invoiceId) => {
    setStagedInvoiceIds((prev) => {
      const exists = prev.includes(invoiceId);
      if (exists) {
        return prev.filter((id) => id !== invoiceId);
      }
      return [...prev, invoiceId];
    });
  };
  const handleToDateInput = (value) => {
    setToDate(value);
    if (value && value === fromDate) {
      setSelectedDay(value);
      setActivePreset(`day:${value}`);
      focusCalendarDate(new Date(value));
      clearCalendarSelection();
    } else {
      setSelectedDay(null);
      setActivePreset(null);
    }
  };
  const handlePrintAllRoutes = React.useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from", fromDate);
    if (toDate) params.append("to", toDate);
    if (statusFilter?.length) {
      statusFilter.forEach((status) => params.append("status", status));
    }
    params.append("print", "1");
    const qs = params.toString();
    window.open(`/routes/print${qs ? `?${qs}` : ""}`, "_blank");
  }, [fromDate, toDate, statusFilter]);

  const plansLoading = routePlansQuery.isLoading;
  const plansFetching = routePlansQuery.isFetching;
  const candidatesLoading = routeCandidatesQuery.isLoading;
  const candidatesFetching = routeCandidatesQuery.isFetching;
  const readyInvoices = React.useMemo(() => {
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const calculatedStart =
      fromDate != null && fromDate !== ""
        ? new Date(fromDate).setHours(0, 0, 0, 0)
        : toDate != null && toDate !== ""
        ? new Date(toDate).setHours(0, 0, 0, 0)
        : today.getTime();
    const calculatedEnd =
      toDate != null && toDate !== ""
        ? new Date(toDate).setHours(23, 59, 59, 999)
        : fromDate != null && fromDate !== ""
        ? new Date(fromDate).setHours(23, 59, 59, 999)
        : today.getTime();

    const rangeStart = Math.min(calculatedStart, calculatedEnd) - oneWeek;
    const rangeEnd = Math.max(calculatedStart, calculatedEnd) + oneWeek;

    return candidateInvoices
      .filter((invoice) => {
        const dueTime = invoice.dueDate ? new Date(invoice.dueDate).getTime() : null;
        if (typeof dueTime !== "number") return false;
        return dueTime >= rangeStart && dueTime <= rangeEnd;
      })
      .slice(0, 20);
  }, [candidateInvoices, fromDate, toDate]);

  const routePlanMutation = useMutation({
    mutationFn: async ({ values, plan }) => {
      const payload = {
        routeDate: values.routeDate,
        name: values.name,
        driverName: values.driverName,
        status: values.status,
        notes: values.notes,
      };

      if (plan) {
        const { data } = await updateRoutePlan(plan._id, payload);
        return data;
      }

      if (!currentUserId) {
        throw new Error("You must be signed in to create a route.");
      }

      const { data } = await createRoutePlan({ ...payload, userId: currentUserId });
      return data;
    },
    onSuccess: () => {
      setEditorState((prev) => ({
        open: false,
        plan: null,
        defaultDate: prev?.defaultDate || todayIso,
      }));
      setEditorError(null);
      queryClient.invalidateQueries({ queryKey: ["routePlans"] });
      queryClient.invalidateQueries({ queryKey: ["routePlanCandidates"] });
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to save route.";
      setEditorError(message);
    },
  });

  const assignInvoicesMutation = useMutation({
    mutationFn: async ({ plan, invoiceIds }) => {
      if (!plan?._id) {
        throw new Error("Select a route before assigning invoices.");
      }
      if (!invoiceIds?.length) {
        throw new Error("Choose at least one invoice to assign.");
      }

      const existingStops = (plan.stops || []).map((stop, index) => ({
        invoice:
          typeof stop.invoice === "object"
            ? stop.invoice?._id || stop.invoice?.id
            : stop.invoice,
        order:
          typeof stop.order === "number" ? stop.order : index,
        notes: stop.notes || "",
        status: stop.status || "pending",
      }));

      const baseOrder =
        existingStops.length > 0
          ? Math.max(...existingStops.map((stop) => stop.order || 0)) + 1
          : 0;

      const newStops = invoiceIds.map((invoiceId, idx) => ({
        invoice: invoiceId,
        order: baseOrder + idx,
        status: "pending",
      }));

      const sanitizedExistingStops = existingStops.filter(
        (stop) => Boolean(stop.invoice)
      );

      const payload = {
        stops: [...sanitizedExistingStops, ...newStops],
      };

      const { data } = await updateRoutePlan(plan._id, payload);
      return data;
    },
    onSuccess: (_, variables) => {
      setAssignError(null);
      setAssignState({ open: false, plan: null, initialSelection: [] });
      queryClient.invalidateQueries({ queryKey: ["routePlans"] });
      queryClient.invalidateQueries({ queryKey: ["routePlanCandidates"] });
      const assignedIds = (variables?.invoiceIds || []).map((id) => String(id));
      if (assignedIds.length) {
        setStagedInvoiceIds((prev) =>
          prev.filter((id) => !assignedIds.includes(String(id)))
        );
      }
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message || error?.message || "Failed to assign invoices.";
      setAssignError(message);
    },
  });

  const stopActionMutation = useMutation({
    mutationFn: async ({ commands }) => {
      for (const command of commands) {
        if (!command?.planId) continue;
        const serializedStops = serializeStops(command.stops || []);
        await updateRoutePlan(command.planId, { stops: serializedStops });
      }
    },
    onSuccess: () => {
      setMoveState((current) =>
        current.open ? { open: false, fromPlan: null, stopIndex: null } : current
      );
      setStopActionError(null);
      queryClient.invalidateQueries({ queryKey: ["routePlans"] });
      queryClient.invalidateQueries({ queryKey: ["routePlanCandidates"] });
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message || error?.message || "Failed to update stops.";
      setStopActionError(message);
    },
  });

  const markRouteLoadedMutation = useMutation({
    mutationFn: async (plan) => {
      if (!plan?._id) throw new Error("Route id missing");
      const payload = { status: plan.status === "completed" ? "completed" : "loading" };
      const { data } = await updateRoutePlan(plan._id, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routePlans"] });
      queryClient.invalidateQueries({ queryKey: ["routePlanCandidates"] });
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message || error?.message || "Failed to mark route loaded.";
      setStopActionError(message);
    },
  });

  const deleteRouteMutation = useMutation({
    mutationFn: async (plan) => {
      if (!plan?._id) throw new Error("Route id missing");
      await deleteRoutePlan(plan._id);
    },
    onSuccess: (_, plan) => {
      if (drawerState.planId === plan?._id) {
        closeRouteDrawer();
      }
      queryClient.invalidateQueries({ queryKey: ["routePlans"] });
      queryClient.invalidateQueries({ queryKey: ["routePlanCandidates"] });
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message || error?.message || "Failed to delete route.";
      setStopActionError(message);
    },
  });

  const assignButtonDisabled =
    !candidateInvoices.length ||
    assignInvoicesMutation.isLoading ||
    stopActionMutation.isLoading;

  const handleRefresh = () => {
    routePlansQuery.refetch();
    routeCandidatesQuery.refetch();
  };

  const toggleStatusFilter = (status) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status]
    );
  };

  const openCreateDialog = (preferredDate) => {
    const fallbackDate = preferredDate || selectedDay || todayIso;
    setEditorError(null);
    setEditorState({ open: true, plan: null, defaultDate: fallbackDate });
  };

  const openEditDialog = (plan) => {
    setEditorError(null);
    const planDate = plan?.routeDate ? toISODate(plan.routeDate) : selectedDay || todayIso;
    setEditorState({ open: true, plan, defaultDate: planDate });
  };

  const closeEditor = () => {
    setEditorError(null);
    setEditorState({ open: false, plan: null, defaultDate: selectedDay || todayIso });
  };

  const handleEditorSubmit = (values) => {
    if (!values.routeDate) {
      setEditorError("Route date is required.");
      return;
    }
    routePlanMutation.mutate({ values, plan: editorState.plan });
  };

  const openAssignDialog = (plan, prefillIds) => {
    setAssignError(null);
    const initialSelection =
      prefillIds && prefillIds.length ? prefillIds : stagedInvoiceIds;
    setAssignState({ open: true, plan, initialSelection });
  };

  const closeAssignDialog = () => {
    setAssignError(null);
    setAssignState({ open: false, plan: null, initialSelection: [] });
  };

  const handleAssignInvoices = (invoiceIds) => {
    if (!assignState.plan) return;
    assignInvoicesMutation.mutate({
      plan: assignState.plan,
      invoiceIds,
    });
  };

  const handleMarkRouteLoaded = (plan) => {
    if (!plan || markRouteLoadedMutation.isLoading) return;
    markRouteLoadedMutation.mutate(plan);
  };

  const handleDeleteRoute = (plan) => {
    if (!plan || deleteRouteMutation.isLoading) return;
    const routeName = plan.name || formatShortDate(plan.routeDate);
    const confirmed = window.confirm(
      `Delete route "${routeName}"? This action cannot be undone.`
    );
    if (!confirmed) return;
    deleteRouteMutation.mutate(plan);
  };

  const handleDrawerAssignStops = () => {
    if (activeDrawerPlan) {
      openAssignDialog(activeDrawerPlan);
    }
  };

  const extractPlanIdFromDroppable = (droppableId) =>
    droppableId?.includes(":") ? droppableId.split(":").pop() : droppableId;

  const handleStopDragEnd = (result) => {
    if (!result.destination || stopActionMutation.isLoading) return;
    const sourcePlan = plansById.get(extractPlanIdFromDroppable(result.source.droppableId));
    const destPlan = plansById.get(extractPlanIdFromDroppable(result.destination.droppableId));
    if (!sourcePlan || !destPlan) return;

    const sourceStops = [...(sourcePlan.stops || [])];
    const [movedStop] = sourceStops.splice(result.source.index, 1);
    if (!movedStop) return;

    if (sourcePlan._id === destPlan._id) {
      sourceStops.splice(result.destination.index, 0, movedStop);
      stopActionMutation.mutate({
        commands: [{ planId: sourcePlan._id, stops: sourceStops }],
      });
      return;
    }

    const destStops = [...(destPlan.stops || [])];
    destStops.splice(result.destination.index, 0, movedStop);
    stopActionMutation.mutate({
      commands: [
        { planId: sourcePlan._id, stops: sourceStops },
        { planId: destPlan._id, stops: destStops },
      ],
    });
  };

  const handleRemoveStop = (planId, stopIndex) => {
    if (stopActionMutation.isLoading) return;
    const plan = plansById.get(planId);
    if (!plan) return;
    const updatedStops = (plan.stops || []).filter((_, idx) => idx !== stopIndex);
    stopActionMutation.mutate({
      commands: [{ planId, stops: updatedStops }],
    });
  };

  const handleUpdateStopStatus = (planId, stopIndex, status) => {
    if (stopActionMutation.isLoading) return;
    const plan = plansById.get(planId);
    if (!plan) return;
    const updatedStops = (plan.stops || []).map((stop, idx) =>
      idx === stopIndex ? { ...stop, status } : stop
    );
    stopActionMutation.mutate({
      commands: [{ planId, stops: updatedStops }],
    });
  };

  const openMoveStopDialog = (plan, stopIndex) => {
    if (plans.length <= 1) return;
    setMoveState({ open: true, fromPlan: plan, stopIndex });
  };

  const closeMoveStopDialog = () => {
    setMoveState({ open: false, fromPlan: null, stopIndex: null });
  };

  const handleMoveStopSubmit = (targetRouteId) => {
    if (!moveState.fromPlan || moveState.stopIndex == null) return;
    const sourcePlan = plansById.get(moveState.fromPlan._id);
    const targetPlan = plansById.get(targetRouteId);
    if (!sourcePlan || !targetPlan) return;

    const sourceStops = [...(sourcePlan.stops || [])];
    const [movedStop] = sourceStops.splice(moveState.stopIndex, 1);
    if (!movedStop) return;
    const targetStops = [...(targetPlan.stops || [])];
    targetStops.push(movedStop);

    stopActionMutation.mutate({
      commands: [
        { planId: sourcePlan._id, stops: sourceStops },
        { planId: targetPlan._id, stops: targetStops },
      ],
    });
  };

  return (
    <DragDropContext onDragEnd={handleStopDragEnd}>
      <Box className={styles.pageRoot}>
      <Box className={styles.pageInner}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 16 }}>
          <Typography variant="h4">Route Planner</Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrintAllRoutes}
              disabled={!plans.length}
            >
              Print all routes
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => openCreateDialog(selectedDay || todayIso)}
              disabled={!currentUserId}
            >
              New Route
            </Button>
          </Box>
        </Box>

        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 16 }}>
          {quickFilterPresets.map((preset) => (
            <Chip
              key={preset.id}
              label={preset.label}
              clickable
              color={activePreset === preset.id ? "primary" : "default"}
              onClick={() => handlePresetSelect(preset.id)}
            />
          ))}
        </Box>
        <Box mt={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <IconButton aria-label="Previous month" onClick={handleCalendarPrev}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {calendarAnchorDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
            </Typography>
            <IconButton aria-label="Next month" onClick={handleCalendarNext}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <div className={styles.calendarWrapper}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
              headerToolbar={false}
              initialView={calendarView}
              firstDay={1}
              height="auto"
              dayMaxEventRows={3}
              selectable
              initialDate={selectedDay || todayIso}
              events={calendarEvents}
              dateClick={handleCalendarDateClick}
              select={handleCalendarRangeSelect}
              eventClick={handleEventClick}
              dayCellClassNames={dayCellClassNames}
            />
          </div>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          style={{ gap: 16, marginTop: 16, marginBottom: 16 }}
        >
          <TextField
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => handleFromDateInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => handleToDateInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            disabled={plansFetching || candidatesFetching}
          >
            Refresh
          </Button>
        </Box>

        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 24 }}>
          {statusOptions.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              color={statusFilter.includes(option.value) ? "primary" : "default"}
              onClick={() => toggleStatusFilter(option.value)}
            />
          ))}
          {statusFilter.length > 0 && (
            <Button size="small" onClick={() => setStatusFilter([])}>
              Clear
            </Button>
          )}
        </Box>
        {stopActionError && (
          <Typography color="error" variant="body2" style={{ marginBottom: 16 }}>
            {stopActionError}
          </Typography>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <CandidateListCard
              invoices={candidateInvoices}
              readyInvoices={readyInvoices}
              stagedInvoiceIds={stagedInvoiceIds}
              loading={candidatesLoading}
              refreshing={candidatesFetching}
              onToggleStage={toggleStagedInvoice}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {plansLoading ? (
              <Typography>Loading route plans…</Typography>
            ) : groupedPlans.length === 0 ? (
              <Typography color="textSecondary">
                No route plans for the selected range.
              </Typography>
            ) : (
              groupedPlans.map((group) => (
                <Box key={group.dateKey} mb={3}>
                    <Typography variant="h6" gutterBottom>
                      {group.label}
                    </Typography>
                    <Grid container spacing={2}>
                      {group.plans.map((plan) => (
                        <Grid item xs={12} key={plan._id}>
                          <RouteCard
                            plan={plan}
                            onEdit={openEditDialog}
                            onAssignStops={openAssignDialog}
                            onOpenDrawer={(p) => openRouteDrawer(p._id)}
                            onMarkLoaded={handleMarkRouteLoaded}
                            onDeleteRoute={handleDeleteRoute}
                            assignDisabled={
                              assignButtonDisabled || markRouteLoadedMutation.isLoading
                            }
                            deleteDisabled={deleteRouteMutation.isLoading}
                          >
                            <PlanStopList
                              plan={plan}
                              droppableId={`card:${plan._id}`}
                              onStatusChange={handleUpdateStopStatus}
                              onRemoveStop={handleRemoveStop}
                              onMoveStop={openMoveStopDialog}
                              disabled={stopActionMutation.isLoading}
                            />
                          </RouteCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))
            )}
          </Grid>
        </Grid>
      </Box>

      <RouteEditorDialog
        open={editorState.open}
        initialValues={editorState.plan}
        onClose={closeEditor}
        onSubmit={handleEditorSubmit}
        isSaving={routePlanMutation.isLoading}
        errorMessage={editorError}
        defaultDate={editorState.defaultDate}
      />
      <AssignInvoicesDialog
        open={assignState.open}
        plan={assignState.plan}
        invoices={candidateInvoices}
        onClose={closeAssignDialog}
        onSubmit={handleAssignInvoices}
        isSubmitting={assignInvoicesMutation.isLoading}
        errorMessage={assignError}
        initialSelection={assignState.initialSelection}
      />
      <MoveStopDialog
        open={moveState.open}
        fromPlan={moveState.fromPlan}
        stopIndex={moveState.stopIndex}
        routes={plans}
        onClose={closeMoveStopDialog}
        onSubmit={handleMoveStopSubmit}
        isSubmitting={stopActionMutation.isLoading}
      />
      <RouteDrawer
        plan={activeDrawerPlan}
        open={drawerState.open}
        onClose={closeRouteDrawer}
        onAssignStops={handleDrawerAssignStops}
        assignDisabled={assignButtonDisabled}
      >
        {activeDrawerPlan && (
          <PlanStopList
            plan={activeDrawerPlan}
            droppableId={`drawer:${activeDrawerPlan._id}`}
            onStatusChange={handleUpdateStopStatus}
            onRemoveStop={handleRemoveStop}
            onMoveStop={openMoveStopDialog}
            disabled={stopActionMutation.isLoading}
          />
        )}
      </RouteDrawer>
      </Box>
    </DragDropContext>
  );
};

export default RoutePlannerPage;
